// Libraries
import { useMemo } from "react";

// Components
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import CustomCheckbox from "../../inputs/CustomCheckbox/CustomCheckbox";
import { CaretDownIcon, CaretUpIcon } from "src/assets/icons";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

// Types
import { OrderType } from "src/types/sharedTypes";

type TableHeaderType<T> = {
  order: OrderType;
  orderBy: keyof T;
  numSelected: number;
  rowCount: number;
  headCells: Array<{
    id: string;
    label?: string;
    gridSize: number;
    orderUsing: Array<keyof T>;
    isCheckbox?: boolean;
  }>;
  extraCells?: Array<{ id: string; label: string; gridSize: number }>;
  onRequestSort?: (
    e: React.MouseEvent<HTMLButtonElement>,
    property: Array<keyof T>
  ) => void;
  onSelectAllClick?: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void;
};

const TableHeader = <T,>({
  order,
  orderBy,
  numSelected,
  rowCount,
  headCells = [],
  extraCells = [],
  onRequestSort = () => {},
  onSelectAllClick = () => {},
}: TableHeaderType<T>) => {
  const theme = useTheme();
  const { onSurface } = getSurfaceTokens(theme);

  const extraGridSize = useMemo(() => {
    let s = 0;
    extraCells.forEach((cell) => (s += cell?.gridSize ?? 0));
    return s;
  }, [extraCells]);

  const caretIconStyle = {
    display: "flex",
    alignItems: "center",
    width: "14px",
    height: "14px",
    marginLeft: "4px",
  };

  return (
    <Grid
      container
      sx={{
        display: { xs: "none", sm: "flex" },
        justifyContent: "space-between",
        visibility: rowCount > 0 ? "visible" : "hidden",
        px: "16px",
      }}
    >
      <Grid container item xs={12 - extraGridSize} alignItems="center">
        {headCells.map((headCell) => {
          const displayCheckbox = Boolean(headCell.isCheckbox);
          const displayButton = Boolean(!headCell.isCheckbox && headCell.label);
          const displayCaretUp =
            orderBy === headCell.orderUsing[0] && order === "asc";
          const isChecked = numSelected === rowCount;
          const isIndeterminate = numSelected > 0 && numSelected < rowCount;

          return (
            <Grid key={headCell.id} item xs={headCell.gridSize}>
              {displayCheckbox && (
                <CustomCheckbox
                  id={`select-all-checkbox-${headCell.id}`}
                  checkedFill={
                    isChecked || isIndeterminate
                      ? theme.palette.mode === "dark"
                        ? "#E2772E"
                        : "#202338"
                      : "#FFF"
                  }
                  checkedStroke={
                    isChecked || isIndeterminate ? "#FFF" : "#202338"
                  }
                  checked={isChecked}
                  indeterminate={isIndeterminate}
                  onClick={onSelectAllClick}
                />
              )}
              {displayButton && (
                <Button
                  sx={{
                    color: onSurface,
                    textTransform: "none",
                    fontSize: "14px",
                    minWidth: 0,
                    maxWidth: "100%",
                    pointerEvents:
                      headCell.orderUsing?.length > 0 ? "auto" : "none",
                    p: 0,
                  }}
                  onClick={
                    headCell.orderUsing?.length > 0
                      ? (e) => onRequestSort(e, headCell.orderUsing)
                      : () => {}
                  }
                >
                  <Typography
                    maxWidth="100%"
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    sx={{
                      fontSize: "14px",
                      paddingRight: "4px",
                    }}
                  >
                    {headCell.label}
                  </Typography>

                  <Box sx={caretIconStyle}>
                    {displayCaretUp ? <CaretUpIcon /> : <CaretDownIcon />}
                  </Box>
                </Button>
              )}
            </Grid>
          );
        })}
      </Grid>

      {/* Extra cells include things like action buttons */}
      <Grid container item xs={extraGridSize}>
        {extraCells.map((headCell) => (
          <Grid key={headCell?.id} item xs={0} lg={headCell?.gridSize} />
        ))}
      </Grid>
    </Grid>
  );
};

export default TableHeader;
